import { createContext, ReactNode, useContext, useState } from "react";
import { ChemistryPlayerPositions, FormationData, TeamChemistry } from "../types/IOption";
import { ISynergyData, TeamResultSummary } from "../pages/Chemistry/Utils";
import { IDisabledButtonsState } from "../types/ITimeline";

interface ISquadSystemFitContextType {
  selectedTeam: number;
  setSelectedTeam: React.Dispatch<React.SetStateAction<number>>;
  positionResults: ChemistryPlayerPositions[];
  setPositionResults: React.Dispatch<React.SetStateAction<ChemistryPlayerPositions[]>>;
  teamChemistry: TeamChemistry[];
  setTeamChemistry: React.Dispatch<React.SetStateAction<TeamChemistry[]>>;
  selectedSeasonId: number;
  setSelectedSeasonId: React.Dispatch<React.SetStateAction<number>>;
  timeLineTag: string;
  setTimeLineTag: React.Dispatch<React.SetStateAction<string>>;
  seasonName: string;
  setSeasonName: React.Dispatch<React.SetStateAction<string>>;
  zoomLevelId: 1 | 2 | 3 | 4;
  setZoomLevelId: React.Dispatch<React.SetStateAction<1 | 2 | 3 | 4>>;
  formationData: FormationData[];
  setFormationData: React.Dispatch<React.SetStateAction<FormationData[]>>;
  selectedFormation: number;
  setSelectedFormation: React.Dispatch<React.SetStateAction<number>>;
  isSwapActive: boolean;
  setIsSwapActive: React.Dispatch<React.SetStateAction<boolean>>;
  isIndividualChemistry: boolean;
  setIsIndividualChemistry: React.Dispatch<React.SetStateAction<boolean>>;
  lastPositionResults: ChemistryPlayerPositions[];
  setLastPositionResults: React.Dispatch<React.SetStateAction<ChemistryPlayerPositions[]>>;
  lastTeamChemistry: TeamChemistry[];
  setLastTeamChemistry: React.Dispatch<React.SetStateAction<TeamChemistry[]>>;
  swappedPlayerClub: string | undefined;
  setSwappedPlayerClub: React.Dispatch<React.SetStateAction<string | undefined>>;
  otherClubPlayer: number;
  setOtherClubPlayer: React.Dispatch<React.SetStateAction<number>>;
  playerToSwap: number;
  setPlayerToSwap: React.Dispatch<React.SetStateAction<number>>;
  swappedPositionResults: ChemistryPlayerPositions[];
  setSwappedPositionResults: React.Dispatch<React.SetStateAction<ChemistryPlayerPositions[]>>;
  swappedTeamChemistry: TeamChemistry[];
  setSwappedTeamChemistry: React.Dispatch<React.SetStateAction<TeamChemistry[]>>;
  thirdOfSeason: string;
  setThirdOfSeason: React.Dispatch<React.SetStateAction<string>>;
  overallSFScore: number;
  setOverallSFScore: React.Dispatch<React.SetStateAction<number>>;
  newOverallSFScore: number;
  setNewOverallSFScore: React.Dispatch<React.SetStateAction<number>>;
  avgAge: number;
  setAvgAge: React.Dispatch<React.SetStateAction<number>>;
  newAvgAge: number;
  setNewAvgAge: React.Dispatch<React.SetStateAction<number>>;
  attackContribution: number;
  setAttackContribution: React.Dispatch<React.SetStateAction<number>>;
  newAttackContribution: number;
  setNewAttackContribution: React.Dispatch<React.SetStateAction<number>>;
  currentPlayerView: number;
  setCurrentPlayerView: React.Dispatch<React.SetStateAction<number>>;
  playerToSwapWith: number;
  setPlayerToSwapWith: React.Dispatch<React.SetStateAction<number>>;
  leagueValues: TeamResultSummary;
  setLeaguevalues: React.Dispatch<React.SetStateAction<TeamResultSummary>>;
  disabledButtons: IDisabledButtonsState;
  setDisabledButtons: React.Dispatch<React.SetStateAction<IDisabledButtonsState>>;
  ppda: number;
  setPPDA: React.Dispatch<React.SetStateAction<number>>;
  newPPDA: number;
  setNewPPDA: React.Dispatch<React.SetStateAction<number>>;
  selectedByPlayerPair: string;
  setSelectedByPlayerPair: React.Dispatch<React.SetStateAction<string>>;
  synergyData: ISynergyData[];
  setSynergyData: React.Dispatch<React.SetStateAction<ISynergyData[]>>;
  overallSystemFitScore: number;
  setOverAllSystemFitScore : React.Dispatch<React.SetStateAction<number>>;
}

const SquadSystemFitContext = createContext<Partial<ISquadSystemFitContextType>>({});

export const useSquadSystemFitContext = (): ISquadSystemFitContextType => {
  const context = useContext(SquadSystemFitContext);
  if (!context) {
    throw new Error("useSquadSystemFitContext must be used within a SquadSystemFitContextProvider");
  }
  return context as ISquadSystemFitContextType;
};

export const SquadSystemFitContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedTeam, setSelectedTeam] = useState<number>(0);
  const [positionResults, setPositionResults] = useState<ChemistryPlayerPositions[]>([]);
  const [teamChemistry, setTeamChemistry] = useState<TeamChemistry[]>([]);
  const [lastPositionResults, setLastPositionResults] = useState<ChemistryPlayerPositions[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<number>(0);
  const [timeLineTag, setTimeLineTag] = useState<string>("");
  const [seasonName, setSeasonName] = useState<string>("");
  const [zoomLevelId, setZoomLevelId] = useState<1 | 2 | 3 | 4>(4);
  const [formationData, setFormationData] = useState<FormationData[]>([]);
  const [selectedFormation, setSelectedFormation] = useState<number>(0);
  const [isSwapActive, setIsSwapActive] = useState<boolean>(false);
  const [isIndividualChemistry, setIsIndividualChemistry] = useState<boolean>(false);
  const [lastTeamChemistry, setLastTeamChemistry] = useState<TeamChemistry[]>([]);
  const [swappedPlayerClub, setSwappedPlayerClub] = useState<string>();
  const [otherClubPlayer, setOtherClubPlayer] = useState<number>(0);
  const [playerToSwap, setPlayerToSwap] = useState<number>(0);
  const [swappedPositionResults, setSwappedPositionResults] = useState<ChemistryPlayerPositions[]>([]);
  const [swappedTeamChemistry, setSwappedTeamChemistry] = useState<TeamChemistry[]>([]);
  const [thirdOfSeason, setThirdOfSeason] = useState<string>("FinalThird");
  const [overallSFScore, setOverallSFScore] = useState<number>(0);
  const [newOverallSFScore, setNewOverallSFScore] = useState<number>(0);
  const [avgAge, setAvgAge] = useState<number>(0);
  const [newAvgAge, setNewAvgAge] = useState<number>(0);
  const [attackContribution, setAttackContribution] = useState<number>(0);
  const [newAttackContribution, setNewAttackContribution] = useState<number>(0);
  const [ppda, setPPDA] = useState<number>(0);
  const [newPPDA, setNewPPDA] = useState<number>(0);
  const [currentPlayerView, setCurrentPlayerView] = useState<number>(0);
  const [playerToSwapWith, setPlayerToSwapWith] = useState<number>(0);
  const [leagueValues, setLeaguevalues] = useState<TeamResultSummary>({
    leaguePosition: 0,
    matchesWon: 0,
    matchesLost: 0,
    matchesDraw: 0,
    totalGoals:0,
    totalGoalsAgainst:0,
    xGoals:0,
    xGoalAgainst:0,
    coaches:[]
  });
  const [disabledButtons, setDisabledButtons] = useState<IDisabledButtonsState>({
    play: true,
    previous: true,
    zoomOut: true,
    zoomIn: true,
    next: true,
  });
  const [selectedByPlayerPair, setSelectedByPlayerPair] = useState<string>("skill");
  const [synergyData , setSynergyData] = useState<ISynergyData[]>([]);
  const [overallSystemFitScore , setOverAllSystemFitScore] = useState<number>(0);
  
  return (
    <SquadSystemFitContext.Provider
      value={{
        selectedTeam, setSelectedTeam,
        positionResults, setPositionResults,
        teamChemistry, setTeamChemistry,
        selectedSeasonId, setSelectedSeasonId,
        timeLineTag, setTimeLineTag,
        seasonName, setSeasonName,
        zoomLevelId, setZoomLevelId,
        formationData, setFormationData,
        selectedFormation, setSelectedFormation,
        isSwapActive, setIsSwapActive,
        isIndividualChemistry, setIsIndividualChemistry,
        lastPositionResults, setLastPositionResults,
        lastTeamChemistry, setLastTeamChemistry,
        swappedPlayerClub, setSwappedPlayerClub,
        otherClubPlayer, setOtherClubPlayer,
        playerToSwap, setPlayerToSwap,
        swappedPositionResults, setSwappedPositionResults,
        swappedTeamChemistry, setSwappedTeamChemistry,
        thirdOfSeason, setThirdOfSeason,
        overallSFScore, setOverallSFScore,
        newOverallSFScore, setNewOverallSFScore,
        avgAge, setAvgAge,
        newAvgAge, setNewAvgAge,
        attackContribution, setAttackContribution,
        newAttackContribution, setNewAttackContribution,
        currentPlayerView, setCurrentPlayerView,
        playerToSwapWith, setPlayerToSwapWith,
        leagueValues, setLeaguevalues,
        disabledButtons, setDisabledButtons,
        ppda, setPPDA,
        newPPDA, setNewPPDA,
        selectedByPlayerPair, 
        setSelectedByPlayerPair,
        synergyData , 
        setSynergyData,
        overallSystemFitScore , setOverAllSystemFitScore
      }}
    >
      {children}
    </SquadSystemFitContext.Provider>
  );
};
